import type { CheckoutFlow } from './checkout-flow-context.server';

/**
 * Where an in-app checkout link points.
 *
 * Each flow has its own route subtree (`…/scheduled/*`, `…/open/*`), so a link
 * to "the time step" can only be built once the flow is known. The pre-
 * separation URLs (`…/dates`, `…/info`) still resolve, but only as redirect
 * shims — routing an internal navigation through one costs an extra round
 * trip, so every in-app link resolves the flow and targets the real step.
 *
 * The literals are spelled out per flow (rather than interpolating the flow
 * segment) so `href()` / `localeHref()` keep type-checking the path against
 * the generated route map.
 *
 * The flows no longer have the same steps: the open flow has a `tickets` and
 * a `location` step (`…/open/tickets`, `…/open/location`) that the scheduled
 * flow has no counterpart for. Which one an open-flow visitor lands on is the
 * dispatcher's call, decided by the basket: an empty basket opens on tickets,
 * one the booking panel already ticketed opens on location.
 */
export const FLOW_STEP_PATHS = {
  scheduled: {
    date: '/:locale?/checkout/:experienceId/scheduled/date',
    time: '/:locale?/checkout/:experienceId/scheduled/time',
    info: '/:locale?/checkout/:experienceId/scheduled/info',
  },
  open: {
    tickets: '/:locale?/checkout/:experienceId/open/tickets',
    location: '/:locale?/checkout/:experienceId/open/location',
    date: '/:locale?/checkout/:experienceId/open/date',
    time: '/:locale?/checkout/:experienceId/open/time',
    info: '/:locale?/checkout/:experienceId/open/info',
  },
} as const satisfies Record<CheckoutFlow, Record<string, string>>;

/** The steps a given flow has. Per-flow, because the flows no longer share a
 *  step list — only the open flow has a `location` step. */
type FlowStep<F extends CheckoutFlow> = keyof (typeof FLOW_STEP_PATHS)[F];

/**
 * A flow step's path segment alone — e.g. `flowStepSegment('open', 'time')
 * === 'open/time'` — for the rare in-app link that has to be built by string
 * append rather than through `href()` (e.g. a cross-domain owner host for a
 * shared experience). Derived from `FLOW_STEP_PATHS` so the two can't drift.
 */
export function flowStepSegment<F extends CheckoutFlow>(
  flow: F,
  step: FlowStep<F>
): string {
  // The generic index needs the widening: `FLOW_STEP_PATHS[flow]` is a union of
  // both flows' step maps, which TS won't index with a per-flow key.
  const path = (FLOW_STEP_PATHS[flow] as Record<string, string>)[
    step as string
  ];
  return path?.split(':experienceId/').at(-1) ?? '';
}

/**
 * A step path with the query string appended, or the bare path when there is
 * nothing to carry.
 *
 * The open flow's links all carry the whole current query forward — `?locationId`
 * is load-bearing on every step behind the time step, and `?from`,
 * `distributorId` and the deep-link params have to survive a trip backwards too
 * — so this ternary sits on every one of them.
 */
export function withStepQuery(path: string, search: string): string {
  return search ? `${path}?${search}` : path;
}

/**
 * The open flow's current selection as a query string — what any link *out* of a
 * step has to carry so the visitor's location survives the trip.
 *
 * More than `searchParams.toString()` because the loader-validated location can
 * differ from the raw param: one derived from the basket, or the experience's
 * only one, isn't in the URL at all, so it is written out explicitly. Everything
 * else in the query (`distributorId`, `?from`, deep-link params) rides along
 * untouched.
 *
 * The duration is NOT part of this: it lives in the basket
 * (`basket.selection.durationSeconds`), so a link carries it implicitly.
 */
export function buildSelectionSearch({
  currentSearch,
  locationId,
}: {
  currentSearch: string;
  locationId: string | null;
}): string {
  const next = new URLSearchParams(currentSearch);
  if (locationId) next.set('locationId', locationId);
  return next.toString();
}

/**
 * A flow step's URL, built from a **bare checkout-entry URL** by appending the
 * step segment ahead of the query string.
 *
 * The entry URL (`buildCheckoutUrl`) is built server-side and is absolute on the
 * owner's host for a shared (connect) experience — so a link to a step of that
 * experience's flow can't go through `href()` / `localeHref()` (they only build
 * same-origin paths). String append is the only option; the query has to survive
 * it because that's where `distributorId` and any deep-link params live.
 *
 * Linking the step directly rather than the entry URL only saves the entry
 * route's dispatch redirect — the destination is identical.
 */
export function checkoutStepHref<F extends CheckoutFlow>(
  checkoutEntryHref: string,
  flow: F,
  step: FlowStep<F>
): string {
  const [path = '', query] = checkoutEntryHref.split('?');
  const base = `${path.replace(/\/+$/, '')}/${flowStepSegment(flow, step)}`;
  return query ? `${base}?${query}` : base;
}

/**
 * Route id of the checkout layout (`checkout.layout.tsx`), for
 * `useRouteLoaderData` reads of its `closeHref`. React Router derives a route's
 * default id from its file path relative to `app/`, minus the extension — so
 * this string has to track the file's location. A spec in
 * `__test__/checkout-links.spec.ts` fails if the two drift apart.
 */
export const CHECKOUT_LAYOUT_ROUTE_ID =
  'routes/checkout/booking/checkout.layout';
